<template>
    <BasePanel 
        :headline="getPanelHeadline"
        :alvqa="alvqa"
        :helpContext="getHelpContext"
    >
        <template v-slot:panelContent>
            <v-data-table
                :headers="headers"
                :items="originalShares"
                hide-default-footer
                :loading="isLoading"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                :header-props="{ sortIcon: '' }"
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="itemsPerPage"
            >
            <!-- Topslot -->
                <template v-slot:top>
                    <v-row class="pb-3">
                        <v-spacer></v-spacer>
                        <v-col cols="3">
                            <BaseButton 
                                @click="addNewRow"
                                :disabled="$route.params.alvId === 'new'"
                                label="lang_GENERAL_BUTTON_ADD_ROW"
                                block
                                :alvqa="alvqa + '-button-add-row'"
                                
                            />
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
                <template v-slot:[`item.name`]="{ item, index }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="getInterestedPartyDisplayName(item) + ' (' +  getRolePosition(item.role, item.position) + ')'"
                            :alvqa="alvqa +'-list-row-' + index + '-text-name'"
                        />
                    </div>
                    <div class="ellipsis subtitleGray--text">
                        <BaseTooltip 
                            :simpleText="getIpiAlvIdForInterestedParty(item)"
                            :alvqa="alvqa +'-list-row-' + index + '-text-ipi'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.pwr`]="{ item, index }">
                    <div :alvqa="alvqa +'-list-row-' + index + '-text-uvz'">
                        {{ getPwrCommaSeparated(item.pwr) }}
                    </div>
                </template>
                <template v-slot:[`item.role`]="{ item, index }">
                    <div :alvqa="alvqa +'-list-row-' + index + '-text-role'">
                        {{ item.role }}
                    </div>
                </template>
                <template v-slot:[`item.societyPrMr`]="{ item, index }">
                    <div class="ellipsis">
                        <BaseTooltip 
                            :simpleText="showSocietyNames(item.prSociety, item.mrSociety)"
                            :alvqa="alvqa +'-list-row-' + index + '-text-society-pr-mr'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.prPercent`]="{ item, index }">
                    <div :alvqa="alvqa +'-list-row-' + index + '-text-percent-own-pr'">
                        {{ $alvPlugin.getNumberLocalization(item.prPercent, 'number') }}
                    </div>
                </template>
                <template v-slot:[`item.mrPercent`]="{ item, index }">
                    <div :alvqa="alvqa +'-list-row-' + index + '-text-percent-own-mr'">
                        {{ $alvPlugin.getNumberLocalization(item.mrPercent, 'number') }}
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.delete`]="{ item, index }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + '-list-row-' + index + '-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item, index }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-' + index + '-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>

            <!-- ********* SUM ROW ********* -->
                <template slot="body.append" v-if="typeName === 'intl' || typeName === 'own'">
                    <tr> 
                        <td></td>
                        <td></td>
                        <td></td>
                        <td align="left" class="data-table-sum-row">{{ $t('lang_GENERIC_CAPTION_SUM') }}</td>
                        <!-- First Sum Column (PR) -->
                        <td align="right" class="data-table-sum-row" v-if="originalShares.length === 0 || sumField('prPercent') === null"><div>{{ $t("lang_GENERIC_CAPTION_NOMEN_NOMINANDUM") }}</div></td>
                        <td align="right" class="data-table-sum-row" v-else-if="sumField('prPercent') !== '100.00'"><div id="not100">{{ $n(sumField('prPercent'),"number") }}</div></td>
                        <td align="right" class="data-table-sum-row" v-else><div>{{ $n(sumField('prPercent'),"number") }}</div></td>
                        <!-- Second Sum Column (MR) -->
                        <td align="right" class="data-table-sum-row" v-if="originalShares.length === 0 || sumField('mrPercent') === null"><div>{{ $t("lang_GENERIC_CAPTION_NOMEN_NOMINANDUM") }}</div></td>
                        <td align="right" class="data-table-sum-row" v-else-if="sumField('mrPercent') !== '100.00'"><div id="not100">{{ $n(sumField('mrPercent'),"number") }}</div></td>
                        <td align="right" class="data-table-sum-row" v-else><div>{{ $n(sumField('mrPercent'),"number") }}</div></td>
                        <td></td>
                        <td></td>
                    </tr>
                </template>
            </v-data-table>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseButton from '@/components/_buttons/BaseButton'

export default {
    name: "OriginalSharesEditPanel",
    components: {
        BasePanel,
        BaseTooltip,
        BaseIcon,
        BaseButton
    },
    props: {
        originalShares: Array,
        typeName: String,
    },
    data() {
        return {
            alvqa: 'publishing-works-edit-form-panel-original-shares-' + this.typeName,
            hasContextHelp: false
        }
    },
    
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_INTERESTED_PARTY"),
                    value: "name", 
                    align: "left", 
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-header-name"
                },
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_ROLE"), 
                    value: "role", 
                    align: "left", 
                    width: "80px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-header-role"
                },
                {
                    text: this.$t("lang_PUBLISHING_WORKS_CAPTION_UVZ"), 
                    align: "left", 
                    value: "pwr", 
                    width: "90px",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + "-header-uvz"
                },
                {
                    text: this.$t('lang_PUBLISHING_WORKS_CAPTION_SOCIETY_PR_MR'), 
                    value: "societyPrMr",
                    align: "left",
                    class: 'text-no-wrap',
                    sortable: false,
                    width: "140px",
                    alvqa: this.alvqa + '-header-society-pr-mr'
                },
                {
                    text: this.getCaptionPercent("pr"), 
                    align: "right", 
                    value: "prPercent", 
                    class: 'text-no-wrap', 
                    width: "90px",
                    sortable: false,
                    alvqa: this.alvqa + "-header-manu-percent-pr"
                },
                {
                    text: this.getCaptionPercent("mr"), 
                    align: "right", 
                    value: "mrPercent", 
                    class: 'text-no-wrap', 
                    width: "90px",
                    sortable: false,
                    alvqa: this.alvqa + "-header-manu-percent-mr"
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },
        getPanelHeadline() {
            return this.typeName.toUpperCase() + '-' + this.$t('lang_GENERAL_CAPTION_SHARES')
            // return this.typeName.toUpperCase() + '-' + this.$t('lang_PUBLISHING_WORKS_CAPTION_ORIGINAL_SHARES')
        },
        getHelpContext() {
            const capitalizedType = this.typeName.charAt(0).toUpperCase() + this.typeName.slice(1)
            return `PublishingWorksEditPanel${capitalizedType}Shares`
        },
        
        isLoading() { return this.$store.getters.getIsLoading },	
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
    },

    methods: {
        getInterestedPartyDisplayName(item) {
            if (['E', 'SE'].includes(item.role)) {
                return item.azEntity.group || item.azEntity.gemaName
            }
            return item.azEntity.gemaName
        },
        getRolePosition(role, position) {
            let str = ''

            if(role !==  null) str += role
            if(position !== null) str += position

            return str
        },
        getPwrCommaSeparated(pwrArr) {
            let pwr = []
            pwrArr.forEach(element => {
                if(element === null) return null
                else pwr.push(element.role+element.position)
            });

            return pwr.join(', ')
        },
        getIpiAlvIdForInterestedParty(item) {
            let str = ''
            if(item.ipi !== '' && item.ipi !== null) {
                str += item.ipi
                if(item.azEntity.alvId !== '' && item.azEntity.alvId !== null) {
                    str += ' | ' + item.azEntity.alvId
                    return str
                }
            }

            if(item.azEntity.alvId !== '' && item.azEntity.alvId !== null) {
                str += item.azEntity.alvId
                return str
            }
        },
        getInfoCol(item) {
            if (this.item.contracts) { // safeguard against contracts being "null" from BE
                let contract = this.item.contracts.find(contract => {
                    return contract.licensor.alvId === item.azEntity.alvId
                })
                if(contract === undefined) return ''
                else return contract.contractType
            } else return ''      
        },
        showSocietyNames(societyPr, societyMr) {
            let societyNameAr = this.$alvPlugin.getSocietyName(societyPr)
            let societyNameMr = this.$alvPlugin.getSocietyName(societyMr)

            if(societyNameAr == '' && societyNameMr === '') return ''
            else return societyNameAr + ' | ' + societyNameMr
        },

        addNewRow(){
            this.$store.commit('setWorksOriginalSharesDetailsShareType', this.typeName)
            this.$store.commit('setShowWorksOriginalSharesEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultWorksOriginalShares)) 
            this.$store.commit('setWorksOriginalSharesEditingDialogDetails', defaultValues)
        },

        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedWorksOriginalShares", item.id)
            this.$store.commit('setDeletionDialogContext', 'SelectedWorksOriginalShares')
            this.$store.commit('setShowDeletionDialog', true)
        },
        editInlineFunction(item) {
            this.$store.commit('setSelectedWorksOriginalShares', item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            // formatting numbers for the editing dialog
            detailsDeepCopy['mrPercent'] = this.$alvPlugin.getNumberLocalization(detailsDeepCopy['mrPercent'], 'number')
            detailsDeepCopy['prPercent'] = this.$alvPlugin.getNumberLocalization(detailsDeepCopy['prPercent'], 'number')
            
            // prepare autocomplete for editing dialog
            if(detailsDeepCopy.azEntity !== null) this.$store.commit('setWorksOriginalSharesInterestedPartyOptions', [detailsDeepCopy.azEntity])

            // set the copy in dialog details to edit
            this.$store.commit('setWorksOriginalSharesEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) // set headline
            this.$store.commit('setWorksOriginalSharesDetailsShareType', this.typeName)
            this.$store.commit('setShowWorksOriginalSharesEditingDialog', true) 
        },
        isUnsaved() {
            let alvId = this.$store.getters.getWorkDetailsAlvId
            if(alvId === 'ALV000000000PW') return true
            else return false
        },
        getCaptionPercent(type) {
            const shareTypeTranslations = [
                {text: this.$t('lang_GENERIC_CAPTION_MANU_PERCENT'), value: "manu"},
                {text: this.$t('lang_GENERIC_CAPTION_ROLE_PERCENT'), value: "role"},
                {text: this.$t('lang_GENERIC_CAPTION_INTL_PERCENT'), value: "intl"},
                {text: this.$t('lang_GENERIC_CAPTION_OWN_PERCENT'), value: "own"},
            ]

            const rightsTypeTranslations = [
                {text: this.$t('lang_PUBLISHING_CAPTION_PERFORMING_RIGHTS_ABBREVIATION'), value: "pr"},
                {text: this.$t('lang_PUBLISHING_CAPTION_MECHANICAL_RIGHTS_ABBREVIATION'), value: "mr"},
            ]
            
            let captionShareType = shareTypeTranslations.find((el) => el.value === this.typeName)
            captionShareType = captionShareType ? captionShareType.text : ""
            let captionsRightsType = rightsTypeTranslations.find((el) => el.value === type)
            captionsRightsType = captionsRightsType ? captionsRightsType.text : ""

            return `${captionShareType} ${captionsRightsType}`
        },
        sumField(column) {
            if (!this.originalShares || !Array.isArray(this.originalShares)) {
                return null
            }
            
            let listOfShares = this.originalShares
            let sum = 0
            let hasValues = false
            
            for (const share of listOfShares) {
                if(share && share[column] !== null && share[column] !== undefined){
                    let value = share[column]
                    
                    // Convert string to number if needed
                    if(typeof value === 'string') {
                        // Replace comma with dot for German locale
                        value = value.replace(',', '.')
                        value = parseFloat(value)
                    }
                    
                    // Check if we have a valid number
                    if(typeof value === 'number' && !isNaN(value)) {
                        sum += value
                        hasValues = true
                    }
                } 
            }
            
            if (!hasValues) {
                return null
            }
            
            return sum.toFixed(2)
        },
        
        
    },
    
    
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
    #not100{
        color:  var(--v-error-base)
    }
</style>